"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type OutputFormat = "lines" | "comma";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

/*
 * A minus is a sign only at the start of the text or right after whitespace or an opening
 * bracket or quote. Anywhere else it is a hyphen or dash: "2024-05-12" is 2024, 5 and 12,
 * and "10-20" is 10 and 20, not 10 and -20. The typeset minus U+2212 counts as a sign too.
 */
const NUMBER_REGEX = /(?:(?<=^|[\s([{"'“‘])[-\u2212])?\d+(?:\.\d+)?/g;
const TYPESET_MINUS_REGEX = /\u2212/;
const THOUSANDS_COMMA_REGEX = /(\d),(?=\d{3}\b)/g;

export function extractNumbers(text: string, stripCommas: boolean): number[] {
  const source = stripCommas ? text.replace(THOUSANDS_COMMA_REGEX, "$1") : text;
  const matches = source.match(NUMBER_REGEX) ?? [];
  return matches.map((m) =>
    Number.parseFloat(m.replace(TYPESET_MINUS_REGEX, "-"))
  );
}

function formatStat(value: number, formatNum: NumberFormatter): string {
  const rounded = Math.round(value * 10_000) / 10_000;
  return formatNum(rounded, { maximumFractionDigits: 4 });
}

function computeStats(
  numbers: number[],
  formatNum: NumberFormatter
): {
  sum: string;
  average: string;
  min: string;
  max: string;
} {
  if (numbers.length === 0) {
    return { sum: "0", average: "0", min: "0", max: "0" };
  }
  const sum = numbers.reduce((acc, n) => acc + n, 0);
  return {
    sum: formatStat(sum, formatNum),
    average: formatStat(sum / numbers.length, formatNum),
    min: formatStat(Math.min(...numbers), formatNum),
    max: formatStat(Math.max(...numbers), formatNum),
  };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const EXTRACT_NUMBERS_STRINGS = {
  onePerLine: "One per line",
  commaSeparated: "Comma-separated",
  options: "Options",
  treatThousandsAsOne: "Treat 1,234 as one number",
  textToScan: "Text to scan",
  inputPlaceholder:
    "Revenue grew 12.5% to 1,240 units, up from -3 the prior year.",
  extractedNumbers: "Extracted numbers",
  count: "Count",
  sum: "Sum",
  average: "Average",
  minimum: "Minimum",
  maximum: "Maximum",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
} as const;

export default function ExtractNumbersWidget() {
  const [input, setInput] = useState("");
  const [stripCommas, setStripCommas] = useState(true);
  const [format, setFormat] = useState<OutputFormat>("lines");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(EXTRACT_NUMBERS_STRINGS);

  const fmt = useToolNumber();
  const formatNum = useToolNumber();

  const outputFormats: { value: OutputFormat; label: string }[] = [
    { value: "lines", label: strings.onePerLine },
    { value: "comma", label: strings.commaSeparated },
  ];

  const numbers = extractNumbers(input, stripCommas);
  const values = numbers.map((n) => String(n));
  const output = format === "lines" ? values.join("\n") : values.join(", ");
  const stats = computeStats(numbers, formatNum);

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  return (
    <>
      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.options}</p>
        <div className="flex flex-wrap gap-3">
          {outputFormats.map((f) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={f.value}
            >
              <input
                checked={format === f.value}
                className="accent-primary"
                name="output-format"
                onChange={() => setFormat(f.value)}
                type="radio"
                value={f.value}
              />
              {f.label}
            </label>
          ))}
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={stripCommas}
              className="accent-primary"
              onChange={(e) => setStripCommas(e.target.checked)}
              type="checkbox"
            />
            {strings.treatThousandsAsOne}
          </label>
        </div>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="numbers-input"
        >
          {strings.textToScan}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="numbers-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="numbers-output"
          >
            {strings.extractedNumbers}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="numbers-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {/* Stats */}
      {numbers.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.count} value={fmt(numbers.length)} />
          <StatCard label={strings.sum} value={stats.sum} />
          <StatCard label={strings.average} value={stats.average} />
          <StatCard label={strings.minimum} value={stats.min} />
          <StatCard label={strings.maximum} value={stats.max} />
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
    </>
  );
}
